import { useMemo } from 'react';
import { generateContentTitle, generatePageTitle } from '../utils/pageTitle';

type UseContentTitleOptions = {
  contentType?: string;
};

type UsePageTitleOptions = {
  /** Page number for pagination (e.g., 2 for page 2) */
  page?: number;
  /** Total number of pages for pagination context */
  totalPages?: number;
  /** Search query for search results pages */
  searchQuery?: string;
  /** Additional context to differentiate the page state */
  context?: string;
  /** Custom suffix to append to the title */
  suffix?: string;
};

/**
 * Hook for generating consistent page titles with state differentiation
 */
export const usePageTitle = (title: string, options: UsePageTitleOptions = {}) => {
  return useMemo(() => {
    if (!title) return undefined;

    let finalTitle = title;

    // Add pagination context
    if (options.page && options.page > 1) {
      if (options.totalPages) {
        finalTitle += ` - Page ${options.page} sur ${options.totalPages}`;
      } else {
        finalTitle += ` - Page ${options.page}`;
      }
    }

    // Add search context
    if (options.searchQuery) {
      finalTitle += ` - Résultats pour "${options.searchQuery}"`;
    }

    // Add custom context
    if (options.context) {
      finalTitle += ` - ${options.context}`;
    }

    // Add custom suffix
    if (options.suffix) {
      finalTitle += ` - ${options.suffix}`;
    }

    return generatePageTitle(finalTitle);
  }, [title, options.page, options.totalPages, options.searchQuery, options.context, options.suffix]);
};

/**
 * Hook for generating content page titles (articles, events, etc.) with state differentiation
 */
export const useContentTitle = (contentTitle: string, options: UseContentTitleOptions & UsePageTitleOptions = {}) => {
  return useMemo(() => {
    if (!contentTitle) return undefined;

    let finalTitle = contentTitle;

    // Add pagination context
    if (options.page && options.page > 1) {
      if (options.totalPages) {
        finalTitle += ` - Page ${options.page} sur ${options.totalPages}`;
      } else {
        finalTitle += ` - Page ${options.page}`;
      }
    }

    // Add search context
    if (options.searchQuery) {
      finalTitle += ` - Résultats pour "${options.searchQuery}"`;
    }

    // Add custom context
    if (options.context) {
      finalTitle += ` - ${options.context}`;
    }

    // Add custom suffix
    if (options.suffix) {
      finalTitle += ` - ${options.suffix}`;
    }

    return generateContentTitle(finalTitle, options.contentType);
  }, [
    contentTitle,
    options.contentType,
    options.page,
    options.totalPages,
    options.searchQuery,
    options.context,
    options.suffix
  ]);
};
